/**
 * Session Management for fds-id
 *
 * Handles session ID generation and nonce storage with session binding.
 * Uses sessionStorage (not localStorage) for security - data clears when tab closes.
 *
 * Security:
 * - Session IDs are cryptographically random UUIDs
 * - Nonces are bound to session IDs to prevent replay attacks
 * - Data stored in sessionStorage (ephemeral, single-tab)
 */

const SESSION_ID_KEY = 'fairdrop_session_id'
const NONCE_STORAGE_KEY = 'fairdrop_siwe_nonce'
const NONCE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export interface StoredNonceData {
  nonce: string
  sessionId: string
  expiresAt: number
  createdAt: number
}

/**
 * Get or create a session ID for this browser session
 * Session ID is stored in sessionStorage (cleared when tab closes)
 */
export function getOrCreateSessionId(): string {
  const stored = sessionStorage.getItem(SESSION_ID_KEY)
  if (stored) {
    return stored
  }

  // Generate cryptographically secure session ID
  const sessionId = crypto.randomUUID()
  sessionStorage.setItem(SESSION_ID_KEY, sessionId)
  return sessionId
}

/**
 * Get current session ID without creating one
 */
export function getSessionId(): string | null {
  return sessionStorage.getItem(SESSION_ID_KEY)
}

/**
 * Store nonce with session binding
 * Must use sessionStorage (not localStorage) for security
 */
export function storeNonceWithSession(
  nonce: string,
  sessionId: string,
  expiresAt: number
): void {
  const data: StoredNonceData = {
    nonce,
    sessionId,
    expiresAt,
    createdAt: Date.now(),
  }
  sessionStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(data))
}

/**
 * Store a locally generated nonce (when fds-id unavailable)
 */
export function storeLocalNonce(nonce: string): void {
  const sessionId = getOrCreateSessionId()
  const expiresAt = Date.now() + NONCE_TTL_MS
  storeNonceWithSession(nonce, sessionId, expiresAt)
}

/**
 * Get stored nonce data
 */
export function getStoredNonce(): StoredNonceData | null {
  const stored = sessionStorage.getItem(NONCE_STORAGE_KEY)
  if (!stored) return null

  try {
    return JSON.parse(stored) as StoredNonceData
  } catch {
    return null
  }
}

/**
 * Verify and consume a nonce
 * Returns true if nonce is valid, not expired, and matches session
 * Consumes the nonce (one-time use)
 */
export function verifyAndConsumeNonce(nonce: string): boolean {
  const stored = getStoredNonce()
  if (!stored) return false

  // Verify nonce matches
  if (stored.nonce !== nonce) return false

  // Verify session matches
  const currentSession = getSessionId()
  if (stored.sessionId !== currentSession) return false

  // Verify not expired
  if (Date.now() > stored.expiresAt) {
    sessionStorage.removeItem(NONCE_STORAGE_KEY)
    return false
  }

  // Consume the nonce (one-time use)
  sessionStorage.removeItem(NONCE_STORAGE_KEY)
  return true
}

/**
 * Clear all session data
 */
export function clearSessionData(): void {
  sessionStorage.removeItem(SESSION_ID_KEY)
  sessionStorage.removeItem(NONCE_STORAGE_KEY)
}

/**
 * Check if degraded mode has been notified this session
 */
export function isDegradedModeNotified(): boolean {
  return sessionStorage.getItem('fairdrop_degraded_notified') === 'true'
}

/**
 * Mark degraded mode as notified for this session
 */
export function markDegradedModeNotified(): void {
  sessionStorage.setItem('fairdrop_degraded_notified', 'true')
}
